import { Box, Chip, Typography } from '@mui/material';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import zeplinColor from 'theme/Color';
import { useState } from 'react';
import { LeadKanbanColumnId } from '../../LeadsKanbanColumn';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CustomSearch from 'components/shared/CustomSearch/CustomSearch';

const statusLabels: Record<LeadKanbanColumnId, string> = {
  open: 'Open',
  contacted: 'Contacted',
  'no-response': 'No Response',
  responded: 'Responded',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
};

const statusOptions: LeadKanbanColumnId[] = [
  'open',
  'contacted',
  'no-response',
  'responded',
  'qualified',
  'unqualified',
];

const STATUS_COLOR_MAP: Record<LeadKanbanColumnId, string> = {
  open: zeplinColor.Neutral8,
  contacted: zeplinColor.AlertInfo,
  'no-response': zeplinColor.Blue,
  responded: zeplinColor.Success,
  qualified: zeplinColor.Success,
  unqualified: zeplinColor.Neutral4,
};

export const LeadFields = ({
  loading,

  fieldName,
  fieldValue,
  isChip = false,
  isProfile = false,
  src,
  icon,
  isDropdown = false,
  dropdownOptions = [],
  onDropdownSelect,
  dropdownHeading,
  dropwdownSearch = false,
}: {
  loading?: boolean;
  fieldName: string;
  fieldValue: string;
  isChip?: boolean;
  isProfile?: boolean;
  src?: string;
  icon: string;
  isDropdown?: boolean;
  dropdownOptions?: { Name: string; Id: string; color?: string }[];
  onDropdownSelect?: (fieldKey: string, value: { Name: string; Id: string }) => void;
  dropdownHeading?: string;
  dropwdownSearch?: boolean;
}) => {
  const [statusAnchorEl, setStatusAnchorEl] = useState<HTMLDivElement | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValue, setSelectedValue] = useState<string>(fieldValue);

  const handleDropdownClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setStatusAnchorEl(e.currentTarget);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const filteredOptions =
    dropwdownSearch && searchTerm
      ? dropdownOptions.filter((o) => o.Name.toLowerCase().includes(searchTerm.toLowerCase()))
      : dropdownOptions;

  // e.g. "Lead Status" -> "leadStatus", "Assign To" -> "assignTo"
  const toFieldKey = (name: string) =>
    name
      .split(' ')
      .map((word, i) =>
        i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
      )
      .join('');

  const handleOptionSelect = (option: { Name: string; Id: string }) => {
    setSelectedValue(option.Name);
    const fieldKey = toFieldKey(fieldName);
    onDropdownSelect?.(fieldKey, option);
    setStatusAnchorEl(null);
    setSearchTerm('');
  };

  return (
    <Box sx={{ display: 'flex', gap: '8px' }}>
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <img src={icon} alt={fieldName} width={20} height={20} />
        <Typography
          variant='responsiveBody3'
          sx={{ color: zeplinColor.Neutral8, minWidth: '10rem' }}
        >
          {fieldName}
        </Typography>
      </Box>

      {isProfile ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: isDropdown ? 'pointer' : 'default',
          }}
          onClick={isDropdown ? handleDropdownClick : undefined}
        >
          {src ? (
            <img
              src={src}
              alt={fieldName}
              style={{ borderRadius: '22px', width: '24px', height: '24px' }}
            />
          ) : (
            <Box
              sx={{
                borderRadius: '24px',

                width: { xs: '24px', xl: '24px' },
                height: { xs: '24px', xl: '24px' },
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: zeplinColor.Brand1,
              }}
            >
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                {fieldValue
                  ?.split(' ')
                  .map((value: any, index: number) =>
                    fieldValue && (index === 0 || index === fieldValue?.split(' ').length - 1)
                      ? value[0]
                      : '',
                  )}
              </Typography>
            </Box>
          )}
          <Typography variant='responsiveBody2'>{fieldValue}</Typography>
          {isDropdown && dropdownOptions.length > 0 && (
            <KeyboardArrowDownIcon sx={{ fontSize: '20px', color: zeplinColor.BasicBlack }} />
          )}
        </Box>
      ) : isChip ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            cursor: isDropdown ? 'pointer' : 'default',
          }}
          onClick={isDropdown ? handleDropdownClick : undefined}
        >
          <Chip
            label={
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral1}>
                {selectedValue || fieldValue}
              </Typography>
            }
            sx={{
              backgroundColor: zeplinColor.BasicBlack,
              padding: '5px 12px 5px 12px',
              borderRadius: '50px',
              height: '24px',
              width: 'fit-content',
            }}
          />
          {isDropdown && dropdownOptions.length > 0 && (
            <KeyboardArrowDownIcon sx={{ fontSize: '20px', color: zeplinColor.BasicBlack }} />
          )}
        </Box>
      ) : (
        <Typography variant='responsiveBody2'>{fieldValue}</Typography>
      )}
      <CustomPopOver
        onClick={(e: any) => e.stopPropagation()}
        setAnchorEl={setStatusAnchorEl}
        anchorEl={statusAnchorEl}
        id='simple-popover'
        sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '12px',
            minWidth: '160px',
          }}
        >
          {dropdownHeading && (
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              {dropdownHeading}
            </Typography>
          )}
          {dropwdownSearch && <CustomSearch getProjects={handleSearch} />}
          {filteredOptions.map((option) => (
            <Box
              key={option.Id}
              onClick={() => handleOptionSelect(option)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '6px 8px',
                borderRadius: '8px',
                backgroundColor:
                  selectedValue === option.Name ? zeplinColor.Neutral3 : 'transparent',
                '&:hover': { backgroundColor: zeplinColor.Neutral3 },
              }}
            >
              {option.color && (
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: option.color,
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {option.Name}
              </Typography>
            </Box>
          ))}
        </Box>
      </CustomPopOver>
    </Box>
  );
};
